import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Api, getSession, selectAccount, deleteAccount } from '../../../api/Assinafy';
import { cancelRequest } from '../../../utils/axios';
import {
  getActivePaidSubscriptionRestrictions,
  getDeletionErrorMessages,
  hasForceablePaidSubscriptionRestriction,
} from '../../../utils/deletionRestrictions';
import { Section } from '../../../layout';
import { SessionContext } from '../../../routes/DashboardRoute';
import { Modal, Button, Icon, Dropdown, Table, Form, Input, Alert, Toast, Avatar } from '../../../components';
import { getRoleName } from '../../../utils/User';

/**
 * TODO
 * - table column: access type
 * - table column: plan 
 */

const WorkspaceTab = () => {
  const [accounts, setAccounts] = useState([])
  const [errors, setErrors] = useState([])
  const [toast, setToast] = useState(false)
  const [loading, setLoading] = useState(false)
  const [modalRemove, setModalRemove] = useState(false)
  const [modalForceRemove, setModalForceRemove] = useState(false)
  const [modalNew, setModalNew] = useState(false)
  const [session, setSession] = useContext(SessionContext);
  const cancelToken = cancelRequest()
  const navigate = useNavigate()
  const { state } = useLocation()

  const getAccounts = () => {
    Api('accountUser/index')({
      ...cancelToken.config
    })
      .then(({ status, data: { data } }) => {
        if (status === 200) {
          setAccounts(data)
        }
      })
      .catch(({ response }) => {
        if (response) {
          setErrors(getDeletionErrorMessages(response.data))
        }
      })
  }

  const handleRemoveSuccess = () => {
    getAccounts()
    setModalRemove(false)
    setModalForceRemove(false)
    setLoading(false)
    setToast({
      success: true,
      message: 'Workspace removido com sucesso'
    })
  }

  const handleRemoveError = (response, workspace) => {
    if (!response) {
      return
    }

    setModalRemove(false)
    setLoading(false)

    const responseData = response.data

    if (hasForceablePaidSubscriptionRestriction(responseData, workspace.id)) {
      setModalForceRemove({
        workspace,
        restrictions: getActivePaidSubscriptionRestrictions(responseData, workspace.id),
      })
      return
    }

    const messages = getDeletionErrorMessages(responseData)
    setErrors(messages)
    setToast({
      success: false,
      message: messages[0],
    })
  }

  const removeWorkspace = (workspace, { force = false } = {}) => {
    const { is_delete_allowed } = workspace

    setLoading(true)
    if (!force) {
      setErrors([])
    }

    const runDelete = () =>
      deleteAccount(is_delete_allowed)(
        workspace.id,
        () => setSession(getSession()),
        force && is_delete_allowed ? { force: true } : {}
      )
        .then(({ status }) => {
          if (status === 200) {
            handleRemoveSuccess()
          }
        })
        .catch(({ response }) => handleRemoveError(response, workspace))

    if (force && is_delete_allowed) {
      return Api('subscription/terminate')(workspace.id)
        .then(runDelete)
        .catch((requestError) => {
          setLoading(false)
          setModalForceRemove((current) => ({
            ...current,
            terminationError:
              requestError?.response?.data?.message ||
              'Não foi possível cancelar a assinatura agora. Tente novamente em alguns minutos.',
          }))
        })
    }

    return runDelete()
  }

  const handleRemove = (workspace) => removeWorkspace(workspace)

  const handleForceRemove = () => {
    if (!modalForceRemove?.workspace) {
      return
    }
    removeWorkspace(modalForceRemove.workspace, { force: true })
  }

  useEffect(() => {
    if (state?.toast) {
      setToast({
        message: state.toast
      })
      window.history.replaceState({}, document.title)
    }
  }, [state])

  useEffect(() => {
    getAccounts()
  }, [])

  return (<Section>
    <Alert show={errors.length} variant="warning" onClose={() => setErrors([])}>
      {errors.map((error) => error)}
    </Alert>

    <Section.Header
      title="Workspaces"
      titleAs="h3"
      opening="Temos abaixo a lista com todos os workspaces que você tem acesso."
    >
      {Api('accountUser/addAccount') && <Button variant="primary" onClick={() => setModalNew(true)}>
        <Icon id="icon-add" className="me-1" size="16" />
        NOVO WORKSPACE
      </Button>}
    </Section.Header>

    <Table className="table-bordered table-hover"
      header={[
        { name: 'Nome' },
        { name: 'ID', className: 'd-none d-md-table-cell' },
        { name: 'Tipo de acesso' },
        // { name: 'Plano' },
        { name: '' }
      ]}
      rows={accounts}
      renderRow={(workspace) => {
        return (<tr key={workspace.id}>
          <td>
            <div className="workspace-header"
              onClick={() => selectAccount(workspace.id, () => {
                setSession(getSession())
                navigate('/dashboard')
              })}
            >
              <Avatar placeholder={workspace.name && workspace.name[0]} />
              <div className="workspace-title">{workspace.name}</div>
            </div>
          </td>
          <td className="d-none d-md-table-cell">
            {workspace.id}
          </td>
          <td>
            {getRoleName(workspace?.roles[0])}
          </td>
          {/* <td></td> */}
          <td className="col-action">
            <TableActions
              workspace={workspace}
              setModalRemove={setModalRemove}
            />
          </td>
        </tr>)
      }}
    />

    <ModalRemove
      show={modalRemove}
      modalRemove={modalRemove}
      setModalRemove={setModalRemove}
      handleRemove={() => handleRemove(modalRemove)}
      loading={loading}
    />

    <ModalForceRemove
      show={!!modalForceRemove}
      modalForceRemove={modalForceRemove}
      setModalForceRemove={setModalForceRemove}
      handleForceRemove={handleForceRemove}
      loading={loading}
    />

    <ModalNew show={modalNew}
      setModalNew={setModalNew}
      getAccounts={getAccounts}
    />

    {toast && (
      <Toast
        show
        variant={toast.success ? 'success' : 'warning'}
        onClose={() => setToast(false)}
      >
        {toast.message}
      </Toast>
    )}
  </Section>);
}

const TableActions = ({ workspace, setModalRemove }) => {
  const [session, setSession] = useContext(SessionContext);
  const navigate = useNavigate()
  const dropdownRef = useRef()

  const actionsButtons = [
    {
      id: 'view',
      icon: 'icon-external',
      text: 'ACESSAR',
      onClick: () => selectAccount(workspace.id, () => {
        setSession(getSession())
        navigate('/dashboard')
      })
    },
    {
      icon: workspace.is_delete_allowed ? 'icon-trash' : 'icon-exit',
      text: workspace.is_delete_allowed ? 'EXCLUIR' : 'SAIR',
      onClick: () => setModalRemove(workspace)
    }
  ]

  const { id, icon, text, onClick } = actionsButtons[0]

  return (<div className="table-actions">
    <div className="d-none d-xl-table-cell">
      <Button variant="light"
        className="has-hover"
        as={Button}
        onClick={() => onClick(id)}
      >
        <Icon id={icon} size="18" className="me-1" />
        {text}
      </Button>
    </div>
    <Dropdown
      ref={dropdownRef}
      align="right"
      variant="action"
      button={<Icon id="icon-more_vert" />}
    >
      {actionsButtons.map(({ id, icon, text, onClick }, i) => {
        if (!text) {
          return;
        }

        return (<Dropdown.Item key={i}
          as={Button}
          onClick={() => onClick(id)}
        >
          <Icon id={icon} className="me-1" size="20" />
          {text}
        </Dropdown.Item>)
      })}
    </Dropdown>
  </div>)
}

const ModalNew = ({ show, setModalNew, getAccounts }) => {
  const [session, setSession] = useContext(SessionContext);

  const handleSubmit = (formData) => Api('accountUser/addAccount')(formData, () => {
    setSession(getSession())
    getAccounts()
  })
    .then(({ status }) => {
      if (status === 200) {
        setModalNew(false)
      }
    })

  return (
    <Modal.Alert show={show}
      title="Criando workspace"
      description="Informe abaixo o nome do seu workspace."
      onClose={() => setModalNew(false)}
    >
      <Form
        id="form-workspace-create"
        onSubmit={handleSubmit}
        size="lg"
        ComponentBody={Modal.Body}
        ComponentFooter={Modal.Footer}
        controls={[
          {
            Component: Input,
            name: 'name',
            placeholder: 'Nome do novo Workspace',
            required: true,
            autoFocus: true
          }
        ]}
        buttons={[
          {
            variant: 'primary', submit: true, children: <>
              <Icon id="icon-check" className="me-1" size="16" />
              SALVAR WORKSPACE
            </>
          }
        ]}
      />
    </Modal.Alert>
  )
}

const ModalRemove = ({ show, modalRemove, setModalRemove, handleRemove, loading }) => {
  if (!modalRemove) {
    return null
  }

  const { name, is_delete_allowed } = modalRemove

  let modalDetails = {
    title: `Deseja realmente excluir o workspace ${name}?`,
    description: `Ao remover o workspace ${name}, você perderá todos os documentos dele. A exclusão do workspace não apagará dívidas pendentes.`
  }

  if (!is_delete_allowed) {
    modalDetails = {
      title: `Deseja realmente sair do workspace ${name}?`,
      description: `Você está saindo do workspace ${name}, você irá perder acesso a todos os documentos. Tem certeza que deseja continuar?`
    }
  }

  return (
    <Modal.Alert show={show}
      variant="danger"
      icon="icon-trash"
      title={modalDetails.title}
      description={modalDetails.description}
      onClose={() => setModalRemove(false)}
    >
      <Modal.Footer>
        <Button variant="light"
          onClick={() => setModalRemove(false)}
        >
          CANCELAR
        </Button>
        <Button variant="danger" submit
          onClick={handleRemove}
          loading={loading}
        >
          <Icon id={is_delete_allowed ? 'icon-trash' : 'icon-exit'} className="me-1" size="16" />
          {is_delete_allowed ? 'REMOVER' : 'SAIR'}
        </Button>
      </Modal.Footer>
    </Modal.Alert>
  )
}

const ModalForceRemove = ({
  show,
  modalForceRemove,
  setModalForceRemove,
  handleForceRemove,
  loading,
}) => {
  if (!modalForceRemove) {
    return null
  }

  const { workspace, restrictions, terminationError } = modalForceRemove
  const restrictionMessages = restrictions.map((restriction) => restriction.message).filter(Boolean)

  return (
    <Modal.Alert
      show={show}
      variant="warning"
      icon="icon-warning"
      title={`Excluir o workspace ${workspace.name} mesmo assim?`}
      description={(
        <>
          {restrictionMessages.map((message, index) => (
            <p key={index}>{message}</p>
          ))}
          <p>
            Ao confirmar, a assinatura paga será cancelada imediatamente e o workspace será removido.
          </p>
        </>
      )}
      onClose={() => setModalForceRemove(false)}
    >
      <Modal.Footer>
        {terminationError && <Alert variant="danger" className="w-100">{terminationError}</Alert>}
        <Button variant="light" onClick={() => setModalForceRemove(false)}>
          CANCELAR
        </Button>
        <Button variant="danger" onClick={handleForceRemove} loading={loading}>
          <Icon id="icon-trash" className="me-1" size="16" />
          {terminationError ? 'TENTAR NOVAMENTE' : 'EXCLUIR MESMO ASSIM'}
        </Button>
      </Modal.Footer>
    </Modal.Alert>
  )
}

export default WorkspaceTab;
